import { Link } from 'react-router-dom'
import { api } from '@/lib/api'
import { money, shortDate } from '@/lib/format'
import { useAsync } from '@/lib/useAsync'
import { PageHeader } from '@/components/ui/page'
import { Badge, Card, EmptyState, ErrorState, Loading } from '@/components/ui/primitives'
import { Table, Td, Th, Tr } from '@/components/ui/table'
import type { PortalStatement, StatementStatus } from '@/types/api'

/**
 * Commission earned, per period.
 *
 * Nothing on this list is a forecast. A statement only ever contains commission on
 * payments the customer has already made, so the money is collected before the statement
 * exists - which is also why there are two statuses and not five: there is nothing to
 * part-pay, and Paid is simply whether a live payout points at it.
 */
export function Statements() {
  const statements = useAsync<PortalStatement[]>(
    (signal) => api.get<PortalStatement[]>('/portal/statements', undefined, signal),
    [],
  )

  if (statements.loading && !statements.data) return <Loading />
  if (statements.error) {
    return (
      <ErrorState
        title={statements.error.title}
        detail={statements.error.detail}
        onRetry={statements.reload}
      />
    )
  }

  const rows = statements.data ?? []

  return (
    <>
      <PageHeader
        title="Statements"
        hint="Commission earned on payments Codemax has already received from the customer."
      />

      <Card>
        {rows.length === 0 ? (
          <EmptyState
            title="No statements yet"
            hint="A statement is raised once a customer has paid on a deal you sourced. Commission is earned on payment, not on signature."
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Reference</Th>
                <Th>Period</Th>
                <Th numeric>Lines</Th>
                <Th numeric>Amount</Th>
                <Th>Status</Th>
                <Th>Issued</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((statement) => (
                <Tr key={statement.id}>
                  <Td className="tabular whitespace-nowrap">
                    <Link
                      to={`/statements/${statement.id}`}
                      className="text-accent hover:underline"
                    >
                      {statement.reference}
                    </Link>
                  </Td>
                  <Td className="whitespace-nowrap">
                    {shortDate(statement.periodStart)} &ndash; {shortDate(statement.periodEnd)}
                  </Td>
                  <Td numeric>{statement.lineCount}</Td>
                  <Td numeric className="whitespace-nowrap">
                    {money(statement.totalAmount, statement.currencyCode)}{' '}
                    <span className="text-[11px] text-faint">{statement.currencyCode}</span>
                  </Td>
                  <Td>
                    <StatementBadge status={statement.status} />
                  </Td>
                  <Td className="whitespace-nowrap">{shortDate(statement.issuedAt)}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  )
}

export function StatementBadge({ status }: { status: StatementStatus }) {
  return status === 'Paid' ? (
    <Badge tone="positive">Paid</Badge>
  ) : (
    <Badge tone="warning">Issued</Badge>
  )
}
